'use client';

import { useEffect, useRef, useState } from 'react';
import { cartService } from 'services/api/client';
import type { CartArticleType } from 'services/types';
import { logger, parseStringToNumber } from 'utils';

interface UseProductInCartReturn {
  isInCart: boolean;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Custom hook: useProductInCart
 *
 * Checks if a specific product is already in the shopping cart.
 * Fetches cart articles on mount. Session and user info are initialized
 * server-side via streaming RSC before client hydration, so no gating
 * on sessionInitialized / isUserInfoReady is required.
 *
 * @param productId - The product ID to check (can be string or number)
 */
export const useProductInCart = (productId?: string | number): UseProductInCartReturn => {
  const [isInCart, setIsInCart] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Skip if already fetched (prevents double call in React Strict Mode)
    if (hasFetchedRef.current) return;

    const checkCartStatus = async () => {
      if (!productId) {
        setIsInCart(false);

        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const cartArticles = await cartService.readCartArticles();
        const numericProductId = parseStringToNumber(productId);
        const found = cartArticles?.data.some((article: CartArticleType) => article.product.productId === numericProductId);

        setIsInCart(found || false);
      } catch (err) {
        const error = err as Error;
        const unknownErrorString = 'UnknownError';

        setError(error);
        setIsInCart(false);

        logger.error({
          ns: 'product_in_cart_check',
          type: error.name || unknownErrorString,
          severity: 'low',
          msg: error.message || unknownErrorString,
          loggedFrom: 'useProductInCart',
        });
      } finally {
        setIsLoading(false);
      }
    };

    hasFetchedRef.current = true;
    checkCartStatus();
  }, []);

  return { isInCart, isLoading, error };
};
